import React from "react";
import { LayoutGrid, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ResultsViewToggle — a tight segmented control that switches between the
 * existing List view and the new Mapbox Map view on the search pages.
 *
 * Visual register: a flat two-button pill (List | Map) with icons, sized to
 * sit comfortably above a results count summary. Styled in the GMH design
 * system using design tokens (border, foreground, muted-foreground, etc.)
 * so it inherits each brand's accent palette automatically — no per-brand
 * styling needed.
 *
 * Accessibility:
 *  • Both buttons are real <button>s with descriptive aria-pressed state.
 *  • The active button gets the foreground background; the inactive one
 *    sits flat with a hover that lifts the text colour, matching how the
 *    brand-pill row above it behaves on /search.
 *  • data-testid hooks are stable for the test suite.
 */
const ResultsViewToggle = ({ view, onChange, className }) => {
  const baseBtn =
    "inline-flex items-center gap-2 px-3.5 py-2 text-[12px] uppercase tracking-[0.16em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/50";
  const active = "bg-foreground text-background";
  const inactive = "text-muted-foreground hover:text-foreground";

  return (
    <div
      role="radiogroup"
      aria-label="Results view"
      className={cn(
        "inline-flex items-stretch border border-border bg-card/40 backdrop-blur-sm rounded-[2px] overflow-hidden",
        className
      )}
      data-testid="results-view-toggle"
    >
      <button
        type="button"
        role="radio"
        aria-checked={view === "list"}
        aria-pressed={view === "list"}
        onClick={() => onChange("list")}
        className={cn(baseBtn, view === "list" ? active : inactive)}
        data-testid="view-toggle-list"
      >
        <LayoutGrid size={14} aria-hidden="true" />
        <span>List</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={view === "map"}
        aria-pressed={view === "map"}
        onClick={() => onChange("map")}
        className={cn(baseBtn, "border-l border-border", view === "map" ? active : inactive)}
        data-testid="view-toggle-map"
      >
        <MapPin size={14} aria-hidden="true" />
        <span>Map</span>
      </button>
    </div>
  );
};

export default ResultsViewToggle;
